import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LegalFormalityLastStatusHistoricType,
  LegalFormalityType,
} from 'types/index'
import dayjs from 'dayjs'
import { Popover } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { LegalFormalitiesContext } from 'contexts/legalFormalitiesContext'
import FormalityMoveBtn from '../LegalFormalities/FormalityMoveBtn'

import {
  DisplayCard,
  DisplayCardTitleWrapper,
  DisplayTable,
  FormalityListStatus,
} from './styles'
import {
  CheckOutlined,
  CloseCircleTwoTone,
  FileProtectOutlined,
} from '@ant-design/icons'

interface IProps {
  setActiveTabKey: (tab: string) => void
}

interface FormalityTableType {
  key: React.Key
  title: string
  completedAt?: string
  signedAt?: string
  droppedAt?: string
  status: any
}

const LegalFormalities = (props: IProps) => {
  const { t } = useTranslation()
  const urlParams = useParams()
  const navigate = useNavigate()
  const { setActiveTabKey } = props
  const { legalFormalities } = useContext(LegalFormalitiesContext)

  const formalityStepDate = (
    step: string,
    historics: LegalFormalityLastStatusHistoricType[]
  ) => {
    const lastHistoric = historics.find(historic => historic.final === step)
    return lastHistoric && lastHistoric.lastDate
  }

  const displayDate = (date: string | undefined) =>
    date ? dayjs(date).format('DD/MM/YYYY') : '-'

  const formalityStatus = (formality: LegalFormalityType) => {
    const status = formality.status
    let popoverContent = (
      <div>
        {status === 'legal_authority_refused'
          ? `${t('common:reason')}: ${formality.authorityRefusalReason}`
          : `${t('common:step')}: ${t(
              `legalFormalities:status.name.${status}`
            )}`}
      </div>
    )

    switch (status) {
      case 'flow_aborted':
        return (
          <FormalityListStatus className={'error'}>
            {<CloseCircleTwoTone twoToneColor="red" />}
            {` ${t(
              'contact:resume.cards.legal_formalities.status.aborted',
              ''
            )}`}
          </FormalityListStatus>
        )
      case 'legal_authority_validated':
        return (
          <FormalityListStatus className={'success'}>
            {<CheckOutlined />}
            {` ${t(
              'contact:resume.cards.legal_formalities.status.validated',
              ''
            )}`}
          </FormalityListStatus>
        )
      case 'legal_authority_refused':
        popoverContent = (
          <div>
            {`${t('common:reason')} : ${formality.authorityRefusalReason}`}
          </div>
        )

        return (
          <Popover content={popoverContent}>
            <FormalityListStatus className={'error'}>
              {`${t(
                'contact:resume.cards.legal_formalities.status.refused',
                ''
              )} `}
              {<CloseCircleTwoTone twoToneColor="red" />}
            </FormalityListStatus>
          </Popover>
        )
      default:
        return (
          <Popover content={popoverContent}>
            <FormalityListStatus className={'pending'}>
              {` ${t(
                'contact:resume.cards.legal_formalities.status.running',
                ''
              )}`}
            </FormalityListStatus>
          </Popover>
        )
    }
  }

  const handleSelectFormality = (formalityId: number | React.Key) => {
    navigate(
      `/contacts/${urlParams.contactId}/legal_formalities/${formalityId}`
    )
    setActiveTabKey('legal_formalities')
  }

  const dataSource: FormalityTableType[] = legalFormalities.map(
    (formality: LegalFormalityType): FormalityTableType => ({
      key: formality.id,
      title: t(
        `products:identificationToken.${formality.purchase.identificationToken}`,
        ''
      ),
      completedAt: formalityStepDate(
        'document_generated',
        formality.lastStatusesHistorics
      ),
      signedAt: formalityStepDate(
        'signature_signed',
        formality.lastStatusesHistorics
      ),
      droppedAt: formalityStepDate(
        'legal_authority_dropped',
        formality.lastStatusesHistorics
      ),
      status: formalityStatus(formality),
    })
  )

  const columns: ColumnsType<any> = [
    {
      title: t('contact:resume.cards.legal_formalities.columns.title'),
      dataIndex: 'title',
      key: 'title',
      render: (value, record, index) => {
        return (
          <a onClick={e => handleSelectFormality(record.key)}>{record.title}</a>
        )
      },
    },
    {
      title: t(
        'contact:resume.cards.legal_formalities.columns.completion_date'
      ),
      dataIndex: 'completedAt',
      key: 'completedAt',
      render: (_: string, { completedAt }: FormalityTableType) =>
        displayDate(completedAt),
      sortDirections: ['ascend', 'descend'],
      sorter: (a, b) => {
        return (a.completedAt || '-').localeCompare(b.completedAt || '-')
      },
    },
    {
      title: t('contact:resume.cards.legal_formalities.columns.signature_date'),
      dataIndex: 'signedAt',
      key: 'signedAt',
      render: (_: string, { signedAt }: FormalityTableType) =>
        displayDate(signedAt),
      sortDirections: ['ascend', 'descend'],
      sorter: (a, b) => {
        return (a.signedAt || '-').localeCompare(b.signedAt || '-')
      },
    },
    {
      title: t('contact:resume.cards.legal_formalities.columns.drop_date'),
      dataIndex: 'droppedAt',
      key: 'droppedAt',
      render: (_: string, { droppedAt }: FormalityTableType) =>
        displayDate(droppedAt),
      sortDirections: ['ascend', 'descend'],
      sorter: (a, b) => {
        return (a.droppedAt || '-').localeCompare(b.droppedAt || '-')
      },
    },
    {
      title: t('contact:resume.cards.legal_formalities.columns.status'),
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: t('contact:resume.cards.legal_formalities.columns.actions'),
      key: 'actions',
      render: (_: string, { key }: FormalityTableType) => (
        <FormalityMoveBtn legalFormalityId={key as number} size="small" />
      ),
    },
  ]

  const cardTitle = (
    <DisplayCardTitleWrapper className={'green'}>
      <FileProtectOutlined />
      {` ${t('contact:resume.cards.legal_formalities.title')}`}
    </DisplayCardTitleWrapper>
  )

  return (
    <DisplayCard title={cardTitle}>
      <DisplayTable dataSource={dataSource} columns={columns} />
    </DisplayCard>
  )
}

export default LegalFormalities
